<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useCompaniesStore } from 'dashboard/stores/companies';
import CompanyAPI from 'dashboard/api/companies';
import { debounce } from '@chatwoot/utils';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const route  = useRoute();
const router = useRouter();
const store  = useStore();
const getters = useStoreGetters();
const companiesStore = useCompaniesStore();

const companyId = computed(() => Number(route.params.companyId));

// ── Company data ───────────────────────────────────────────────────────────────
const company   = ref(null);
const loading   = ref(true);

const fieldDefs = computed(() => getters['companyFieldDefinitions/getAll'].value);

const loadCompany = async () => {
  loading.value = true;
  try {
    const { data } = await CompanyAPI.show(companyId.value);
    company.value = data.payload || data;
  } catch {
    useAlert('Failed to load company');
  } finally {
    loading.value = false;
  }
};

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',       label: 'Account details',  icon: 'i-lucide-layout-dashboard' },
  { key: 'contacts',       label: 'Contacts',          icon: 'i-lucide-users' },
  { key: 'tickets',        label: 'Tickets',            icon: 'i-lucide-ticket' },
  { key: 'conversations',  label: 'Conversations',      icon: 'i-lucide-message-square' },
];
const activeTab = ref('overview');

// ── Tab data ──────────────────────────────────────────────────────────────────
const tabData     = ref({ contacts: null, tickets: null, conversations: null });
const tabLoading  = ref({ contacts: false, tickets: false, conversations: false });
const tabPage     = ref({ contacts: 1, tickets: 1, conversations: 1 });
const tabMeta     = ref({ contacts: {}, tickets: {}, conversations: {} });

const loadTabData = async tab => {
  if (tab === 'overview') return;

  tabLoading.value[tab] = true;
  try {
    let resp;
    if (tab === 'contacts')      resp = await CompanyAPI.getContacts(companyId.value, tabPage.value[tab]);
    if (tab === 'tickets')       resp = await CompanyAPI.getTickets(companyId.value, tabPage.value[tab]);
    if (tab === 'conversations') resp = await CompanyAPI.getConversations(companyId.value, tabPage.value[tab]);

    const d = resp.data;
    tabData.value[tab]  = tab === 'contacts' ? d.payload : (d.payload ?? d.data?.payload ?? []);
    tabMeta.value[tab]  = tab === 'contacts' ? d.meta : (d.meta ?? d.data?.meta ?? {});
  } catch {
    tabData.value[tab] = [];
  } finally {
    tabLoading.value[tab] = false;
  }
};

const switchTab = tab => {
  activeTab.value = tab;
  loadTabData(tab);
};

const loadPage = async (tab, page) => {
  if (tab === 'overview') return;
  tabPage.value[tab] = page;
  tabLoading.value[tab] = true;
  try {
    let resp;
    if (tab === 'contacts')      resp = await CompanyAPI.getContacts(companyId.value, page);
    if (tab === 'tickets')       resp = await CompanyAPI.getTickets(companyId.value, page);
    if (tab === 'conversations') resp = await CompanyAPI.getConversations(companyId.value, page);

    const d = resp.data;
    tabData.value[tab] = tab === 'contacts' ? d.payload : (d.payload ?? d.data?.payload ?? []);
    tabMeta.value[tab] = tab === 'contacts' ? d.meta : (d.meta ?? d.data?.meta ?? {});
  } catch {
    // keep existing data
  } finally {
    tabLoading.value[tab] = false;
  }
};

// ── Edit company ──────────────────────────────────────────────────────────────
const editDialogRef = ref(null);
const saving        = ref(false);
const editForm      = ref({ name: '', domain: '', description: '', extraFields: {} });

const openEdit = () => {
  if (!company.value) return;
  const extra = {};
  fieldDefs.value.forEach(fd => {
    extra[fd.field_key] = company.value.extra_fields?.[fd.field_key] || '';
  });
  editForm.value = {
    name: company.value.name || '',
    domain: company.value.domain || '',
    description: company.value.description || '',
    extraFields: extra,
  };
  editDialogRef.value?.open();
};

const saveEdit = async () => {
  const companyDomain = (editForm.value.extraFields.company_domain || '').trim();
  if (!companyDomain) return;
  saving.value = true;
  try {
    const { data } = await CompanyAPI.update(companyId.value, {
      name: (editForm.value.extraFields.company_domain || '').trim(),
      domain: (editForm.value.extraFields.company_domain || '').trim() || null,
      description: editForm.value.description.trim(),
      extra_fields: editForm.value.extraFields,
    });
    const record = data.payload || data;
    company.value = record;
    // sync into the companies list store too
    companiesStore.records = companiesStore.records.map(r =>
      r.id === record.id ? record : r
    );
    useAlert('Company updated');
    editDialogRef.value?.close();
  } catch {
    useAlert('Failed to update company');
  } finally {
    saving.value = false;
  }
};

// ── Company domain autocomplete (for company_domain custom field in edit dialog) ──
const domainSearchResults = ref([]);
const domainSearchLoading = ref(false);
const showDomainDropdown = ref(false);
const domainDropdownStyle = ref({ top: '0px', left: '0px', width: '300px', maxHeight: '200px', zIndex: 10000 });

const searchDomains = debounce(async query => {
  if (!query || query.length < 1) { domainSearchResults.value = []; return; }
  domainSearchLoading.value = true;
  try {
    const { data } = await CompanyAPI.search(query, 1);
    domainSearchResults.value = data.payload || [];
  } catch {
    domainSearchResults.value = [];
  } finally {
    domainSearchLoading.value = false;
  }
}, 300);

const onDomainInput = event => {
  const query = event.target.value;
  showDomainDropdown.value = true;
  const rect = event.currentTarget.getBoundingClientRect();
  domainDropdownStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    maxHeight: '200px',
    zIndex: 10000,
  };
  searchDomains(query);
};

const selectDomainResult = company => {
  editForm.value.extraFields['company_domain'] = company.domain || company.name;
  showDomainDropdown.value = false;
  domainSearchResults.value = [];
};

const closeDomainDropdown = () => { showDomainDropdown.value = false; };

// ── Navigation ────────────────────────────────────────────────────────────────
const goBack = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/companies`);
  }
};

const goToContact = contactId => {
  router.push(`/app/accounts/${route.params.accountId}/contacts/${contactId}`);
};

const goToTicket = ticketId => {
  router.push(`/app/accounts/${route.params.accountId}/tickets/${ticketId}`);
};

const goToConversation = conversationId => {
  router.push(`/app/accounts/${route.params.accountId}/conversations/${conversationId}`);
};

// ── Add Contact ───────────────────────────────────────────────────────────────
const addContactDialogRef  = ref(null);
const addContactFormRef    = ref(null);
const addContactSaving     = ref(false);
const pendingContactData   = ref(null);

const openAddContact = () => {
  pendingContactData.value = null;
  addContactDialogRef.value?.open();
};

const onContactFormUpdate = data => {
  pendingContactData.value = data;
};

const saveNewContact = async () => {
  if (!pendingContactData.value) return;
  addContactSaving.value = true;
  try {
    await store.dispatch('contacts/create', {
      ...pendingContactData.value,
      companyId: companyId.value,
    });
    useAlert('Contact created successfully');
    addContactFormRef.value?.resetForm();
    addContactDialogRef.value?.close();
    tabData.value.contacts = null;
    await loadTabData('contacts');
  } catch (err) {
    const msg = err?.response?.data?.message || 'Failed to create contact';
    useAlert(msg);
  } finally {
    addContactSaving.value = false;
  }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatNumber = (value, fmt = 'comma_dot') => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const [intRaw, decRaw] = num.toString().split('.');
  const dec = decRaw ? decRaw.substring(0, 4) : null;
  let intFormatted;
  if (fmt === 'indian') {
    if (intRaw.length <= 3) { intFormatted = intRaw; }
    else {
      const last3 = intRaw.slice(-3);
      const rest = intRaw.slice(0, -3);
      intFormatted = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }
  } else if (fmt === 'dot_comma') {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  } else if (fmt === 'space_comma') {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  } else {
    intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  const sep = (fmt === 'dot_comma' || fmt === 'space_comma') ? ',' : '.';
  return dec ? `${intFormatted}${sep}${dec}` : intFormatted;
};

const displayCfValue = (fd, rawValue) => {
  if (rawValue == null || rawValue === '') return '—';
  if (fd.field_type === 'checkbox') return rawValue ? 'Yes' : 'No';
  if (fd.field_type === 'number') return formatNumber(rawValue, fd.field_config?.format);
  return rawValue;
};

// Group custom fields by group_name for display
const groupedFieldDefs = computed(() => {
  const groups = {};
  fieldDefs.value.forEach(fd => {
    const g = fd.group_name || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push(fd);
  });
  return groups;
});

const statusColor = s => ({
  open: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  resolved: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  closed: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
}[s] || 'bg-slate-100 text-slate-500');

const priorityColor = p => ({
  urgent: 'text-red-500',
  high:   'text-orange-500',
  medium: 'text-yellow-500',
  low:    'text-blue-400',
}[p] || 'text-n-slate-9');

const priorityIcon = p => ({
  urgent: 'i-lucide-alert-circle',
  high:   'i-lucide-arrow-up',
  medium: 'i-lucide-minus',
  low:    'i-lucide-arrow-down',
}[p] || 'i-lucide-circle-dashed');

const formatDate = ts => {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── Init ──────────────────────────────────────────────────────────────────────
onMounted(async () => {
  await loadCompany();
  store.dispatch('companyFieldDefinitions/fetchAll');
});
</script>

<template>
  <div class="flex flex-1 h-full w-full bg-n-surface-1 overflow-hidden">

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center flex-1">
      <span class="i-lucide-loader-2 animate-spin size-7 text-woot-500" />
    </div>

    <template v-else-if="company">
      <!-- ── Left sidebar ───────────────────────────────────────────────── -->
      <aside class="w-56 shrink-0 border-r border-n-weak flex flex-col h-full overflow-y-auto bg-n-surface-1">
        <!-- Back + identity -->
        <div class="p-4 border-b border-n-weak">
          <button
            class="flex items-center gap-1.5 text-xs text-n-slate-9 hover:text-n-slate-12 mb-3 transition-colors"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-3.5" />
            Companies
          </button>
          <div class="flex items-center gap-2.5">
            <Avatar
              :username="company.name || 'Company'"
              :src="company.avatar_url || null"
              :size="36"
              rounded-full
              class="shrink-0"
            />
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12 truncate">{{ company.name }}</p>
              <p v-if="company.domain" class="text-xs text-n-slate-9 truncate">{{ company.domain }}</p>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 p-2 space-y-0.5">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors text-left"
            :class="activeTab === tab.key
              ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
              : 'text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12'"
            @click="switchTab(tab.key)"
          >
            <span :class="tab.icon" class="size-4 shrink-0" />
            <span>{{ tab.label }}</span>
          </button>
        </nav>
      </aside>

      <!-- ── Main content ───────────────────────────────────────────────── -->
      <div class="flex-1 flex flex-col h-full overflow-hidden">
        <!-- Sticky header -->
        <div class="sticky top-0 z-10 bg-n-surface-1 border-b border-n-weak px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h1 class="text-base font-semibold text-n-slate-12">{{ company.name }}</h1>
            <p v-if="company.domain" class="text-sm text-n-slate-9">{{ company.domain }}</p>
          </div>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="openEdit"
          >
            <span class="i-lucide-pencil size-3.5" />
            Edit
          </button>
        </div>

        <!-- Scrollable content -->
        <div class="flex-1 overflow-y-auto px-6 py-6">

          <!-- ── Account details (overview) ─────────────────────────── -->
          <template v-if="activeTab === 'overview'">
            <!-- Basic information -->
            <div class="mb-8">
              <h2 class="text-sm font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                <span class="i-lucide-info size-4 text-n-slate-9" />
                Basic information
              </h2>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
                <div>
                  <p class="text-xs text-n-slate-9 mb-1">Company Name</p>
                  <p class="text-sm font-medium text-n-slate-12">{{ company.name || '—' }}</p>
                </div>
                <div>
                  <p class="text-xs text-n-slate-9 mb-1">Contacts</p>
                  <p class="text-sm text-n-slate-12">{{ company.contacts_count || 0 }}</p>
                </div>
                <div v-if="company.description" class="col-span-2 md:col-span-3">
                  <p class="text-xs text-n-slate-9 mb-1">Description</p>
                  <p class="text-sm text-n-slate-12">{{ company.description }}</p>
                </div>
              </div>
            </div>

            <!-- Custom fields grouped by section -->
            <template v-if="fieldDefs.length">
              <div
                v-for="(fds, groupName) in groupedFieldDefs"
                :key="groupName"
                class="border-t border-n-weak pt-6 mb-6"
              >
                <h2 class="text-sm font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                  <span class="i-lucide-sliders-horizontal size-4 text-n-slate-9" />
                  {{ groupName }}
                </h2>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div v-for="fd in fds" :key="fd.id">
                    <p class="text-xs text-n-slate-9 mb-1">{{ fd.label }}</p>
                    <p class="text-sm text-n-slate-12">
                      {{ displayCfValue(fd, company.extra_fields?.[fd.field_key]) }}
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <!-- ── Contacts ───────────────────────────────────────────── -->
          <template v-else-if="activeTab === 'contacts'">
            <!-- Header with Add Contact button -->
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-n-slate-12">Contacts</p>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
                @click="openAddContact"
              >
                <span class="i-lucide-plus size-3.5" />
                Add Contact
              </button>
            </div>
            <div v-if="tabLoading.contacts" class="flex justify-center py-12">
              <span class="i-lucide-loader-2 animate-spin size-6 text-woot-500" />
            </div>
            <div v-else-if="!tabData.contacts?.length" class="flex flex-col items-center py-16 text-center">
              <span class="i-lucide-users size-10 text-n-slate-6 mb-3" />
              <p class="text-sm font-medium text-n-slate-9">No contacts linked to this company</p>
              <button
                class="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
                @click="openAddContact"
              >
                <span class="i-lucide-plus size-3.5" />
                Add first contact
              </button>
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="c in tabData.contacts"
                :key="c.id"
                class="flex items-center gap-3 px-4 py-3 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 cursor-pointer hover:border-woot-300 transition-colors"
                @click="goToContact(c.id)"
              >
                <Avatar :username="c.name || c.email" :src="c.thumbnail" :size="36" rounded-full class="shrink-0" />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">{{ c.name || '(no name)' }}</p>
                  <p class="text-xs text-n-slate-9 truncate">{{ c.email }}</p>
                </div>
                <p v-if="c.phone_number" class="text-xs text-n-slate-9 shrink-0 hidden sm:block">{{ c.phone_number }}</p>
                <span class="i-lucide-chevron-right size-4 text-n-slate-7 shrink-0" />
              </div>
              <div v-if="tabMeta.contacts.count > 25" class="flex items-center justify-between pt-4">
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.contacts <= 1" @click="loadPage('contacts', tabPage.contacts - 1)">← Prev</button>
                <span class="text-xs text-n-slate-9">Page {{ tabPage.contacts }}</span>
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.contacts * 25 >= tabMeta.contacts.count" @click="loadPage('contacts', tabPage.contacts + 1)">Next →</button>
              </div>
            </div>
          </template>

          <!-- ── Tickets ────────────────────────────────────────────── -->
          <template v-else-if="activeTab === 'tickets'">
            <div v-if="tabLoading.tickets" class="flex justify-center py-12">
              <span class="i-lucide-loader-2 animate-spin size-6 text-woot-500" />
            </div>
            <div v-else-if="!tabData.tickets?.length" class="flex flex-col items-center py-16 text-center">
              <span class="i-lucide-ticket size-10 text-n-slate-6 mb-3" />
              <p class="text-sm font-medium text-n-slate-9">No tickets for this company</p>
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="t in tabData.tickets"
                :key="t.id"
                class="flex items-start gap-3 px-4 py-3 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 cursor-pointer hover:border-woot-300 transition-colors"
                @click="goToTicket(t.id)"
              >
                <span :class="priorityIcon(t.priority)" class="size-4 mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-xs font-mono text-n-slate-8">#{{ t.id }}</span>
                    <p class="text-sm font-medium text-n-slate-12 truncate">{{ t.subject }}</p>
                  </div>
                  <div class="flex items-center gap-2 mt-1 flex-wrap">
                    <span class="text-xs px-2 py-0.5 rounded-full font-medium" :class="statusColor(t.status)">{{ t.status }}</span>
                    <span v-if="t.assignee" class="text-xs text-n-slate-9">{{ t.assignee.name }}</span>
                    <span class="text-xs text-n-slate-8">{{ formatDate(t.created_at) }}</span>
                  </div>
                </div>
                <span class="i-lucide-chevron-right size-4 text-n-slate-7 shrink-0 mt-0.5" />
              </div>
              <div v-if="tabMeta.tickets.count > 25" class="flex items-center justify-between pt-4">
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.tickets <= 1" @click="loadPage('tickets', tabPage.tickets - 1)">← Prev</button>
                <span class="text-xs text-n-slate-9">Page {{ tabPage.tickets }}</span>
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.tickets * 25 >= tabMeta.tickets.count" @click="loadPage('tickets', tabPage.tickets + 1)">Next →</button>
              </div>
            </div>
          </template>

          <!-- ── Conversations ──────────────────────────────────────── -->
          <template v-else-if="activeTab === 'conversations'">
            <div v-if="tabLoading.conversations" class="flex justify-center py-12">
              <span class="i-lucide-loader-2 animate-spin size-6 text-woot-500" />
            </div>
            <div v-else-if="!tabData.conversations?.length" class="flex flex-col items-center py-16 text-center">
              <span class="i-lucide-message-square size-10 text-n-slate-6 mb-3" />
              <p class="text-sm font-medium text-n-slate-9">No conversations for this company</p>
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="cv in tabData.conversations"
                :key="cv.id"
                class="flex items-center gap-3 px-4 py-3 rounded-xl border border-n-weak bg-white dark:bg-n-solid-1 cursor-pointer hover:border-woot-300 transition-colors"
                @click="goToConversation(cv.display_id)"
              >
                <span class="i-lucide-message-square size-4 text-n-slate-7 shrink-0" />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-xs font-mono text-n-slate-8">#{{ cv.display_id }}</span>
                    <p v-if="cv.contact?.name" class="text-sm font-medium text-n-slate-12 truncate">{{ cv.contact.name }}</p>
                  </div>
                  <div class="flex items-center gap-2 mt-1 flex-wrap">
                    <span class="text-xs px-2 py-0.5 rounded-full font-medium" :class="statusColor(cv.status)">{{ cv.status }}</span>
                    <span v-if="cv.inbox_name" class="text-xs text-n-slate-9">{{ cv.inbox_name }}</span>
                    <span class="text-xs text-n-slate-8">{{ formatDate(cv.created_at) }}</span>
                  </div>
                </div>
                <span class="i-lucide-chevron-right size-4 text-n-slate-7 shrink-0" />
              </div>
              <div v-if="tabMeta.conversations.count > 25" class="flex items-center justify-between pt-4">
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.conversations <= 1" @click="loadPage('conversations', tabPage.conversations - 1)">← Prev</button>
                <span class="text-xs text-n-slate-9">Page {{ tabPage.conversations }}</span>
                <button class="px-3 py-1.5 text-sm text-n-slate-9 border border-n-weak rounded-lg disabled:opacity-40" :disabled="tabPage.conversations * 25 >= tabMeta.conversations.count" @click="loadPage('conversations', tabPage.conversations + 1)">Next →</button>
              </div>
            </div>
          </template>

        </div>
      </div>
    </template>

    <!-- Not found -->
    <div v-else class="flex flex-col items-center justify-center flex-1 py-20">
      <span class="i-lucide-building-2 size-12 text-n-slate-6 mb-3" />
      <p class="text-sm font-medium text-n-slate-9">Company not found</p>
      <button class="mt-3 text-sm text-woot-500 underline" @click="goBack">← Back to Companies</button>
    </div>

    <!-- ── Edit dialog ─────────────────────────────────────────────────────── -->
    <Dialog
      ref="editDialogRef"
      title="Edit Company"
      confirm-button-label="Update"
      cancel-button-label="Cancel"
      :disable-confirm-button="!(editForm.extraFields?.company_domain || '').trim() || saving"
      :is-loading="saving"
      @confirm="saveEdit"
      @close="editDialogRef?.close()"
    >
      <div class="flex flex-col gap-4">
        <div v-for="fd in fieldDefs" :key="fd.id">
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">
            {{ fd.label }}
            <span v-if="fd.field_type === 'auto_number'" class="ml-1 text-n-slate-8 normal-case font-normal">(auto-generated)</span>
            <span v-if="fd.required" class="ml-1 text-red-500">*</span>
          </label>
          <!-- Auto-number: read-only display -->
          <template v-if="fd.field_type === 'auto_number'">
            <div class="px-3 py-2 text-sm rounded-lg border border-n-weak/60 bg-n-alpha-1 text-n-slate-9 font-mono">
              {{ editForm.extraFields[fd.field_key] || '— assigned on save —' }}
            </div>
          </template>
          <template v-else-if="fd.field_type === 'checkbox'">
            <input v-model="editForm.extraFields[fd.field_key]" type="checkbox" class="w-4 h-4 rounded accent-woot-500" />
          </template>
          <template v-else-if="fd.field_type === 'list'">
            <select v-model="editForm.extraFields[fd.field_key]"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500">
              <option value="">— select —</option>
              <option v-for="opt in fd.options" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </template>
          <template v-else-if="fd.field_type === 'date'">
            <input v-model="editForm.extraFields[fd.field_key]" type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500" />
          </template>
          <template v-else-if="fd.field_type === 'number'">
            <input v-model="editForm.extraFields[fd.field_key]" type="number"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500" />
          </template>
          <template v-else-if="fd.field_key === 'company_domain'">
            <input
              v-model="editForm.extraFields[fd.field_key]"
              type="text"
              placeholder="Search company domain…"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @input="onDomainInput"
              @focus="onDomainInput"
            />
          </template>
          <template v-else>
            <input v-model="editForm.extraFields[fd.field_key]" :type="fd.field_type === 'link' ? 'url' : 'text'"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500" />
          </template>
        </div>
      </div>
    </Dialog>

    <!-- Domain autocomplete dropdown (teleported to escape dialog overflow) -->
    <Teleport to="body">
      <div
        v-if="showDomainDropdown && (domainSearchResults.length || domainSearchLoading)"
        v-click-outside="closeDomainDropdown"
        class="fixed bg-n-solid-2 border border-n-weak rounded-lg shadow-xl overflow-y-auto flex flex-col"
        :style="domainDropdownStyle"
      >
        <div v-if="domainSearchLoading" class="text-xs text-n-slate-9 px-3 py-2">Searching…</div>
        <template v-else>
          <button
            v-for="company in domainSearchResults"
            :key="company.id"
            class="flex flex-col items-start px-3 py-2 hover:bg-n-alpha-2 text-left transition-colors"
            @click="selectDomainResult(company)"
          >
            <span class="text-sm font-medium text-n-slate-12">{{ company.domain || company.name }}</span>
            <span v-if="company.domain && company.name" class="text-xs text-n-slate-9">{{ company.name }}</span>
          </button>
        </template>
      </div>
    </Teleport>

    <!-- ── Add Contact dialog ────────────────────────────────────────────── -->
    <Dialog
      ref="addContactDialogRef"
      title="Add Contact"
      confirm-button-label="Create Contact"
      cancel-button-label="Cancel"
      width="3xl"
      :disable-confirm-button="addContactFormRef?.isFormInvalid || !pendingContactData || addContactSaving"
      :is-loading="addContactSaving"
      @confirm="saveNewContact"
      @close="addContactDialogRef?.close()"
    >
      <ContactsForm
        ref="addContactFormRef"
        is-new-contact
        :initial-company-id="companyId"
        :initial-company-domain="company?.domain || ''"
        @update="onContactFormUpdate"
      />
    </Dialog>

  </div>
</template>
